import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { withStoredAdminSubmissionSession } from "../utils/adminWorkSessionStorage";

const ADMIN_NAV_ITEMS: Array<{ label: string; path: string }> = [
  { label: "Home", path: "/" },
  { label: "Admin Submissions", path: "/admin/submissions" },
  { label: "Create - Language", path: "/create/language" },
  { label: "Create - Story Type", path: "/create/story-type" },
  { label: "Create - Photos Upload", path: "/create/photos-upload" },
  { label: "Create - Story Details", path: "/create/story-details" },
  { label: "Create - Review", path: "/create/review" },
  { label: "Create - Story Studio", path: "/create/story-studio" },
  { label: "Create - Photo Training", path: "/create/photo-training" },
  { label: "Create - Character Analysis", path: "/create/character-analysis" },
  { label: "Create - Image Studio", path: "/create/image-studio" },
  { label: "Create - Cover Studio", path: "/create/cover-studio" },
  { label: "Create - Result", path: "/create/result" },
  { label: "Create - Digital Book", path: "/create/digital-book" },
  { label: "Create - Order Summary", path: "/create/order-summary" },
];

export type AdminPageNavigatorProps = {
  /** Place under centered titles vs left-aligned headers */
  align?: "center" | "start";
};

/**
 * Admin-only jump menu. Render directly under the page `<h1>` so the trigger stays in document flow.
 */
const AdminPageNavigator: React.FC<AdminPageNavigatorProps> = ({ align = "center" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, isLoading } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.isAdmin === true;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (isLoading || !isAdmin || !token) return null;

  const rowAlign = align === "start" ? "justify-start" : "justify-center";

  return (
    <div className={`relative z-20 mb-3 flex w-full ${rowAlign}`}>
      <div
        ref={rootRef}
        className="relative inline-flex max-w-[min(100vw-2rem,200px)] rounded-md border border-amber-200/80 bg-amber-50/95 py-0.5 pl-1 pr-1 shadow-sm backdrop-blur-sm"
      >
        <button
          type="button"
          id="admin-page-nav"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls="admin-page-nav-list"
          onClick={() => setOpen((v) => !v)}
          className="flex max-w-[200px] cursor-pointer items-center justify-between gap-1 rounded-sm border-0 bg-amber-50/95 py-0.5 pl-1 pr-0.5 text-left text-[11px] font-bold leading-tight text-amber-950 hover:bg-amber-100/90 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:ring-offset-1 focus:ring-offset-amber-50/95"
        >
          <span className="truncate">Navigate</span>
          <span className="shrink-0 text-[9px] font-bold text-amber-950" aria-hidden>
            {open ? "▴" : "▾"}
          </span>
        </button>
        {open ? (
          <ul
            id="admin-page-nav-list"
            role="listbox"
            aria-labelledby="admin-page-nav"
            className="absolute left-0 top-[calc(100%+2px)] z-[60] m-0 max-h-[min(70vh,320px)] w-max min-w-full list-none overflow-y-auto rounded-md border border-stone-200/90 bg-white p-0 py-1 text-left shadow-lg"
          >
            {ADMIN_NAV_ITEMS.map((item) => {
              const selected = location.pathname === item.path;
              return (
                <li key={item.path} role="presentation" className="list-none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`block w-full cursor-pointer whitespace-nowrap px-2.5 py-1 text-left text-[11px] font-medium hover:bg-amber-50/90 ${
                      selected ? "bg-amber-100/80 text-heading" : "text-heading"
                    }`}
                    onClick={() => {
                      navigate(withStoredAdminSubmissionSession(item.path));
                      setOpen(false);
                    }}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export default AdminPageNavigator;
